import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { isNativeApp, isStandaloneMode } from "@/utils/mobileShell";

const THRESHOLD = 72; // px the user must pull before releasing triggers a refresh
const MAX_PULL = 120;

/**
 * Pull down from the top of a page to reload it, inside the native app shell (and installed PWA).
 * The browser's own pull-to-refresh is disabled by `overscroll-behavior-y: none` on <body>, so
 * this reproduces the gesture: an indicator follows the finger, spins past the threshold, and
 * releasing reloads the current screen.
 */
export default function PullToRefresh() {
  const enabled = typeof window !== "undefined" && (isNativeApp() || isStandaloneMode());
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pulling = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const scrollTop = () => Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop);
    const insideScrollable = (target) => {
      // A horizontally or vertically scrolling element that is not at its top keeps its own gesture.
      let node = target;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        if (/(auto|scroll)/.test(style.overflowY) && node.scrollTop > 0) return true;
        if (node.closest?.("dialog, .swal2-container, [role='dialog']")) return true;
        node = node.parentElement;
      }
      return false;
    };

    const onStart = (event) => {
      if (refreshing || event.touches.length !== 1 || scrollTop() > 0 || insideScrollable(event.target)) {
        startY.current = null;
        return;
      }
      startY.current = event.touches[0].clientY;
      pulling.current = false;
    };
    const onMove = (event) => {
      if (startY.current === null) return;
      const dy = event.touches[0].clientY - startY.current;
      if (dy <= 0 || scrollTop() > 0) {
        if (pulling.current) { pulling.current = false; setPull(0); }
        return;
      }
      pulling.current = true;
      // Resist the pull so it feels like the native gesture.
      setPull(Math.min(MAX_PULL, dy * 0.55));
      if (event.cancelable) event.preventDefault();
    };
    const onEnd = () => {
      if (startY.current === null) return;
      startY.current = null;
      if (!pulling.current) return;
      pulling.current = false;
      setPull((current) => {
        if (current >= THRESHOLD) {
          setRefreshing(true);
          window.setTimeout(() => window.location.reload(), 150);
          return THRESHOLD;
        }
        return 0;
      });
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, refreshing]);

  if (!enabled || (pull <= 0 && !refreshing)) return null;

  const progress = Math.min(1, pull / THRESHOLD);
  return (
    <div
      aria-live="polite"
      aria-label={refreshing ? "Refreshing" : undefined}
      className="pointer-events-none fixed inset-x-0 z-50 flex justify-center"
      style={{ top: `calc(var(--safe-area-top, 0px) + ${Math.round(pull * 0.6)}px)`, transition: refreshing ? "none" : "top 120ms ease-out" }}
    >
      <span
        className="flex size-10 items-center justify-center rounded-full bg-white text-brand shadow-lg ring-1 ring-black/5 dark:bg-slate-800"
        style={{ opacity: Math.max(0.35, progress), transform: `rotate(${Math.round(progress * 270)}deg) scale(${0.7 + progress * 0.3})` }}
      >
        <RefreshCw className={refreshing ? "size-5 animate-spin" : "size-5"} />
      </span>
    </div>
  );
}
